import { useMutation, useQueryClient } from "@tanstack/react-query";
import { actions } from "astro:actions";
import { navigate } from "astro:transitions/client";
import { $path } from "astro-typesafe-routes/path";
import { toast } from "sonner";

import { Checkbox } from "~/components/Checkbox";
import { DialogBody } from "~/components/DialogBody";
import { DialogFooter } from "~/components/DialogFooter";
import { DialogHeader } from "~/components/DialogHeader";
import { DialogTitle } from "~/components/DialogTitle";
import { useModal } from "~/components/Modal";
import type { MyPacksData } from "~/components/MyPacks/MyPacks";
import { trackEvent } from "~/utils/analytics";
import type { FragmentType } from "~/utils/fragment";
import { getPackFullName } from "~/utils/pack";

import type { DeletePack_PackFragment } from "./DeletePack_PackFragment";

export const DeletePack: React.FC<{
  pack: FragmentType<"Pack", { select: typeof DeletePack_PackFragment }>;
}> = ({ pack }) => {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: actions.pack.delete.orThrow,
    onError: (error) => {
      toast.error("Failed to delete pack", {
        description: error.message,
      });
    },
    onSuccess: (data) => {
      trackEvent("pack_deleted");

      const queries = queryClient.setQueriesData<MyPacksData>(
        { queryKey: ["packs"] },
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              packs: page.packs.filter((pack) => pack.id !== data.id),
            })),
          },
      );

      if (!queries.length) {
        navigate(
          $path({
            to: "/account/packs",
            searchParams: {
              message: "Pack deleted",
            },
          }),
        );
      }
    },
  });
  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        mutate(formData);
      }}
    >
      <DialogHeader>Delete pack</DialogHeader>
      <DialogBody className="space-y-4">
        <DialogTitle>Are you sure?</DialogTitle>
        <p>
          Do you really want to delete your pack{" "}
          <strong>{getPackFullName(pack, pack.collection)}</strong>? Assignments
          created with this pack will no longer be playable.
        </p>
        <input type="hidden" name="packId" value={pack.id} />
        <Checkbox name="deleteWords">
          Remove this pack&apos;s words from my word bank (optional)
        </Checkbox>
      </DialogBody>
      <DialogFooter>
        <button type="button" onClick={closeModal} className="btn">
          No, go back
        </button>
        <button
          disabled={isPending || isSuccess}
          type="submit"
          className="btn btn-error"
        >
          Yes, delete pack
        </button>
      </DialogFooter>
    </form>
  );
};
